import { Stack, Typography } from '@mui/material'
import { C1Button, C1IconButton } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { DurationSelect } from 'components/core/time/duration'
import { RoundedDuration } from 'components/core/time/duration/duration'
import { canActOn } from 'components/pages/task/state'
import { useAlert } from 'hooks/useAlert'
import { TaskValue } from 'lib/actions/task'
import { Pencil } from 'lucide-react'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { Duration } from 'pbts/google/protobuf/duration'
import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

type TaskEditDurationProps = {
    task: TaskValue
    appEntitlement: AppEntitlement | undefined
}

export const TaskEditDuration: FC<TaskEditDurationProps> = ({ task, appEntitlement }) => {
    const ticketActionTracker = useTicketActionTracker()
    const toast = useAlert()
    const intl = useIntl()

    const [isEditing, setIsEditing] = useState(false)
    const [durationValue, setDurationValue] = useState<number>(() => {
        // Get current duration from the task's grant duration
        if (task.type?.taskType.oneofKind === 'grant') {
            return Number(task.type.taskType.grant.grantDuration?.seconds || 0)
        }
        return 0
    })

    const canEditDuration = canActOn(task, TaskActionType.UPDATE_GRANT_DURATION)

    let maxDuration
    if (appEntitlement && appEntitlement.maxGrantDuration?.oneofKind === 'durationGrant') {
        maxDuration = appEntitlement.maxGrantDuration.durationGrant.seconds
    } else {
        maxDuration = undefined
    }

    const handleEdit = (): void => {
        if (!canEditDuration) return
        setIsEditing(true)
    }

    const handleSave = async (): Promise<void> => {
        if (!canEditDuration) return

        try {
            await ticketActionTracker?.updateGrantDuration(task, Duration.create({ seconds: durationValue }))

            toast(intl.formatMessage({
                id: 'components_task_details_duration_update_success',
                defaultMessage: 'Grant duration updated successfully',
            }), 'success')
        } catch (error) {
            toast(intl.formatMessage({
                id: 'components_task_details_duration_update_error',
                defaultMessage: 'Failed to update grant duration: {error}',
            }, { error: error instanceof Error ? error.message : 'Unknown error' }), 'error')
        }
        setIsEditing(false)
    }

    const handleCancel = (): void => {
        // Reset to original value
        if (task.type?.taskType.oneofKind === 'grant') {
            setDurationValue(Number(task.type.taskType.grant.grantDuration?.seconds || 0))
        }
        setIsEditing(false)
    }

    const handleDurationChange = (seconds: number): void => {
        setDurationValue(seconds)
    }

    const currentDuration = task.type?.taskType.oneofKind === 'grant'
        ? task.type.taskType.grant.grantDuration?.seconds
        : undefined

    if (isEditing) {
        return (
            <Stack spacing={2} direction="row">
                <DurationSelect
                    onChange={handleDurationChange}
                    value={durationValue || 0}
                    fullWidth
                    maxDuration={maxDuration}
                />
                <Stack direction="row" spacing={1}>
                    <C1Button
                        onClick={handleSave}
                        aria-label="Save duration"
                        variant="contained"
                        color="primary"
                    >
                        <FormattedMessage id="task_edit_duration_save" defaultMessage="Save" />
                    </C1Button>
                    <C1Button
                        onClick={handleCancel}
                        aria-label="Cancel duration"
                        variant="text"
                        color="info"
                    >
                        <FormattedMessage id="task_edit_duration_cancel" defaultMessage="Cancel" />
                    </C1Button>
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>
                {currentDuration ? (
                    <RoundedDuration duration={Duration.create({ seconds: currentDuration })} />
                ) : (
                    <FormattedMessage id="components_task_details_duration_indefinite" defaultMessage="Indefinite" />
                )}
            </Typography>
            {canEditDuration && <C1IconButton
                onClick={handleEdit}
                aria-label="Edit duration"
            >
                <C1Icon icon={Pencil} />
            </C1IconButton>}
        </Stack>
    )
}
